/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  HStack,
  Input,
  InputGroup,
} from "@chakra-ui/react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useTheme } from "../hooks/useTheme";

/**
 * Responsive Table Component with Filtering and Sorting
 * Shows a table on desktop/tablet and cards on mobile
 */
export default function ResponsiveTable({
  columns,
  data,
  renderRow,
  renderCard,
  searchable = false,
  sortable = true,
  filterOptions = null,
  selectedRowIndex = null,
  onRowClick = null,
}) {
  const { colors, isArabic, isDark, dir } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterValue, setFilterValue] = useState("");

  // Filter and sort data
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply search filter
    if (searchable && searchTerm) {
      result = result.filter((row) => {
        return JSON.stringify(row).toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Apply custom filter
    if (filterOptions && filterValue) {
      result = result.filter((row) => {
        const value = row[filterOptions.field];
        return value && value.toString().toLowerCase() === filterValue.toLowerCase();
      });
    }

    // Apply sorting
    if (sortable && sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        // Handle dates
        if (aVal && bVal && (aVal.includes('T') || aVal.includes('-'))) {
          const aDate = new Date(aVal);
          const bDate = new Date(bVal);
          return sortConfig.direction === 'asc'
            ? aDate - bDate
            : bDate - aDate;
        }

        // Handle strings (alphabetical)
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const comparison = aVal.localeCompare(bVal, isArabic ? 'ar' : 'en');
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }

        // Handle numbers
        const comparison = (aVal || 0) - (bVal || 0);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchTerm, filterValue, sortConfig, searchable, sortable, filterOptions, isArabic]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort style={{ opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <Box>
      {/* Filters and Search */}
      {(searchable || filterOptions) && (
        <HStack spacing={4} mb={4} flexWrap="wrap">
          {searchable && (
            <InputGroup flex="1" minW="200px">
              <Input
                placeholder={isArabic ? "بحث..." : "Search..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg={colors.inputBg}
                color={colors.textColor}
                borderColor={colors.inputBorder}
                textAlign={isArabic ? "right" : "left"}
                dir={isArabic ? "rtl" : "ltr"}
                borderRadius="md"
                borderWidth="1px"
                h="38px"
                fontSize="sm"
                _focus={{
                  borderColor: colors.inputFocus,
                  boxShadow: `0 0 0 1px ${colors.inputFocus}`,
                  bg: colors.inputBg
                }}
                _hover={{
                  borderColor: colors.inputFocus,
                  bg: colors.inputBg
                }}
                _dark={{ bg: colors.inputBg, color: colors.textColor }}
                _placeholder={{
                  textAlign: isArabic ? "right" : "left",
                  color: colors.subText,
                  opacity: 0.7
                }}
              />
            </InputGroup>
          )}

          {filterOptions && (
            <Select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              bg={colors.inputBg}
              color={isDark ? "#f1f5f9" : colors.textColor}
              borderColor={colors.inputBorder}
              _focus={{ borderColor: colors.inputFocus, color: isDark ? "#f1f5f9" : colors.textColor }}
              _hover={{ borderColor: colors.inputFocus }}
              textAlign="center"
              borderRadius="md"
              borderWidth="1px"
              fontSize="sm"
              fontWeight="500"
              maxW="200px"
              h="38px"
              cursor="pointer"
              px="12px"
              sx={{
                "&": {
                  color: isDark ? "#f1f5f9 !important" : `${colors.textColor} !important`,
                },
                "& option": {
                  textAlign: "center",
                  bg: colors.inputBg,
                  color: isDark ? "#f1f5f9" : colors.textColor,
                },
                "&::-webkit-appearance": {
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                },
                "&::-ms-expand": {
                  display: "none",
                },
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Cpath fill='${isDark ? '%23f1f5f9' : '%2364748b'}' d='M7 10L2 5h10L7 10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "14px 14px",
              }}
            >
              {filterOptions.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </HStack>
      )}

      {/* Table View - All Screen Sizes */}
      <Box
        overflowX="auto"
        bg={colors.cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={colors.borderColor}
        sx={{
          "&::-webkit-scrollbar": {
            height: "10px",
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: colors.borderColor,
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.mainColor,
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: colors.mainColorHover,
            },
          },
        }}
      >
        <Table
          variant="simple"
          size={{ base: "sm", md: "md" }}
          minW="600px"
          dir={dir}
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <Thead>
            <Tr bg={colors.tableHeaderBg}>
              {columns.map((col, idx) => (
                <Th
                  key={idx}
                  fontWeight="bold"
                  color={colors.textColor}
                  borderBottom={`2px solid ${colors.borderColor}`}
                  py={{ base: 3, md: 4 }}
                  px={{ base: 2, md: 4 }}
                  textAlign={isArabic ? "right" : "left"}
                  whiteSpace="nowrap"
                  cursor={sortable ? "pointer" : "default"}
                  onClick={() => sortable && col.sortKey && handleSort(col.sortKey)}
                  _hover={sortable && col.sortKey ? { bg: colors.hoverBg } : {}}
                  userSelect="none"
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  <Flex align="center" gap={2}>
                    {col.header}
                    {sortable && col.sortKey && (
                      <Box color={colors.mainColor}>
                        {getSortIcon(col.sortKey)}
                      </Box>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {processedData.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py={8} color={colors.subText}>
                  {isArabic ? "لا توجد بيانات" : "No data found"}
                </Td>
              </Tr>
            ) : (
              processedData.map((row, rowIdx) => {
                const isSelected = selectedRowIndex === rowIdx;
                return (
                  <Tr
                    key={rowIdx}
                    bg={
                      isSelected
                        ? isDark
                          ? "rgba(99, 102, 241, 0.22)"
                          : "rgba(99, 102, 241, 0.10)"
                        : rowIdx % 2 === 1
                          ? isDark ? "rgba(255,255,255,0.04)" : "#F1F5F9"
                          : "transparent"
                    }
                    boxShadow={isSelected ? `inset 3px 0 0 ${colors.mainColor}` : "none"}
                    outline={isSelected ? `1.5px solid ${colors.mainColor}` : "none"}
                    outlineOffset="-1.5px"
                    transition="all 0.18s"
                    cursor={onRowClick ? "pointer" : "default"}
                    _hover={{
                      bg: isSelected
                        ? isDark ? "rgba(99,102,241,0.30)" : "rgba(99,102,241,0.16)"
                        : isDark ? "rgba(255,255,255,0.07)" : "#E2E8F0",
                      transition: "0.18s",
                    }}
                    borderBottom={`1px solid ${colors.borderColor}`}
                    onClick={() => onRowClick && onRowClick(row, rowIdx)}
                  >
                    {renderRow(row, rowIdx)}
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
